import { Fragment, useState } from 'react';
import { ChevronDown, ChevronRight, ChevronUp } from 'lucide-react';
import {
  ExpectationStatus,
  ExpectationSummary,
  MeetingExpectation,
  RaceExpectation,
  useExpectedWinners,
} from '../../queries/expectedWinners';
import { Card } from '../../components/ui/Card';
import { Badge, BadgeProps } from '../../components/ui/Badge';

const STATUS: Record<ExpectationStatus, { label: string; variant: BadgeProps['variant'] }> = {
  'not started': { label: 'Not started', variant: 'outline' },
  'in line': { label: 'In line', variant: 'default' },
  above: { label: 'Above', variant: 'success' },
  below: { label: 'Below', variant: 'warning' },
};

const RESULT: Record<RaceExpectation['result'], { label: string; variant: BadgeProps['variant'] }> = {
  won: { label: 'Won', variant: 'success' },
  lost: { label: 'Lost', variant: 'default' },
  pending: { label: 'To run', variant: 'outline' },
  scratched: { label: 'Scratched', variant: 'warning' },
  unpriced: { label: 'No price', variant: 'outline' },
};

const title = (s: string) => (s === s.toLowerCase() ? s.replace(/\b([a-z])/g, (c) => c.toUpperCase()) : s);
const range = (low: number, high: number) => (low === high ? `${low}` : `${low}–${high}`);
const pct = (v: number | null) => (v == null ? '–' : `${v.toFixed(1)}%`);

function StatusBadge({ status }: { status: ExpectationStatus }) {
  const s = STATUS[status];
  return <Badge variant={s.variant}>{s.label}</Badge>;
}

/** e.g. "3 from 6 settled (1.9 expected)" once a race has settled. */
function actualText(s: ExpectationSummary) {
  return `${s.actual_winners} from ${s.settled_races} settled (${s.expected_settled.toFixed(1)} expected)`;
}

function DaySummary({ day }: { day: ExpectationSummary }) {
  return (
    <span className="text-sm text-muted-foreground" data-testid="day-summary">
      {day.tipped_races} tips · {day.expected_winners.toFixed(1)} expected winners ({pct(day.expected_strike_pct)}) · likely{' '}
      {range(day.range_low, day.range_high)}
      {day.p_zero_pct != null && <> · {day.p_zero_pct.toFixed(1)}% chance of none</>}
      {day.settled_races > 0 && <> · so far {actualText(day)}</>}
    </span>
  );
}

function RaceRows({ meeting }: { meeting: MeetingExpectation }) {
  return (
    <tr className="border-b border-border/60 bg-muted/20">
      <td colSpan={8} className="px-3 py-2">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-muted-foreground">
              <th className="py-1 pr-3 font-medium">Race</th>
              <th className="py-1 pr-3 font-medium">Top pick</th>
              <th className="py-1 pr-3 text-right font-medium">Price</th>
              <th className="py-1 pr-3 text-right font-medium">Expected</th>
              <th className="py-1 font-medium">Result</th>
            </tr>
          </thead>
          <tbody>
            {meeting.races.map((r) => (
              <tr key={r.race_id} className={r.result === 'scratched' ? 'text-muted-foreground line-through' : undefined}>
                <td className="py-1 pr-3 tabular-nums">R{r.race_number}</td>
                <td className="py-1 pr-3">
                  {r.tab_number != null && <span className="tabular-nums text-muted-foreground">{r.tab_number}. </span>}
                  {r.horse_name ?? '–'}
                </td>
                <td className="py-1 pr-3 text-right tabular-nums">
                  {r.price == null ? '–' : `$${r.price.toFixed(2)}`}
                  {r.price_source && <span className="ml-1 text-muted-foreground">({r.price_source})</span>}
                </td>
                <td className="py-1 pr-3 text-right tabular-nums">{pct(r.expected_pct)}</td>
                <td className="py-1">
                  <Badge variant={RESULT[r.result].variant}>
                    {RESULT[r.result].label}
                    {r.result === 'lost' && r.position != null ? ` (${r.position})` : ''}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </td>
    </tr>
  );
}

export function ExpectedWinnersPanel({ date }: { date: string }) {
  const { data, isLoading, isError } = useExpectedWinners(date);
  const meetings = data?.meetings ?? [];

  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleMeeting = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Collapsed by default: one line with the day's totals; the explanation and meeting table expand.
  return (
    <Card className="overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-muted/40"
      >
        <span className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5">
          <span className="text-sm font-semibold">Expected winners</span>
          {data && data.day.tipped_races > 0 && (
            <>
              <DaySummary day={data.day} />
              <StatusBadge status={data.day.status} />
            </>
          )}
        </span>
        {open ? (
          <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
      </button>

      {open && (
        <div className="border-t border-border px-4 pb-4 pt-3">
          <p className="mb-3 text-xs text-muted-foreground">
            Expected strike comes from each top pick's price: a $4 pick wins about 1 in 4. Open meetings with longer-priced picks will win
            fewer races without being worse tips. The likely range covers at least 8 in 10 days like this one. The price is the live price where we
            have one, otherwise the price when tipped. Scratched picks are left out. Private and training meetings are
            included and marked, so their tips can be checked before release. After racing, winners are compared with the likely
            range for the races that have been run.
          </p>
          {isLoading ? (
            <p className="py-4 text-sm text-muted-foreground">Loading expected winners…</p>
          ) : isError ? (
            <p className="py-4 text-sm text-destructive">Couldn't load expected winners. Try again shortly.</p>
          ) : meetings.length === 0 ? (
            <p className="py-4 text-sm text-muted-foreground">No top picks for this day yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs text-muted-foreground">
                    <th className="py-2 pr-3 font-medium">Meeting</th>
                    <th className="py-2 pr-3 text-right font-medium">Tips</th>
                    <th className="py-2 pr-3 text-right font-medium">Expected strike</th>
                    <th className="py-2 pr-3 text-right font-medium">Expected winners</th>
                    <th className="py-2 pr-3 text-right font-medium">Likely range</th>
                    <th className="py-2 pr-3 text-right font-medium">Chance of none</th>
                    <th className="py-2 pr-3 font-medium">Actual</th>
                    <th className="py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {meetings.map((m) => {
                    const isOpen = expanded.has(m.meeting_id);
                    return (
                      <Fragment key={m.meeting_id}>
                        <tr className="border-b border-border/60">
                          <td className="py-2 pr-3">
                            <button
                              type="button"
                              onClick={() => toggleMeeting(m.meeting_id)}
                              aria-expanded={isOpen}
                              className="inline-flex items-center gap-1 text-left hover:underline"
                            >
                              {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                              <span className="font-medium">{title(m.track_name)}</span>
                            </button>{' '}
                            {m.state && <span className="text-xs uppercase text-muted-foreground">{m.state}</span>}
                            {m.is_private && <Badge variant="outline" className="ml-1.5">Private</Badge>}
                            {m.is_training && <Badge variant="outline" className="ml-1.5">Training</Badge>}
                          </td>
                          <td
                            className="py-2 pr-3 text-right tabular-nums"
                            title={m.scratched_races ? `${m.scratched_races} scratched top pick(s) left out` : undefined}
                          >
                            {m.tipped_races}
                            {m.scratched_races > 0 && <span className="text-muted-foreground"> (+{m.scratched_races} scr)</span>}
                          </td>
                          <td className="py-2 pr-3 text-right font-semibold tabular-nums">{pct(m.expected_strike_pct)}</td>
                          <td className="py-2 pr-3 text-right tabular-nums">{m.expected_winners.toFixed(1)}</td>
                          <td className="py-2 pr-3 text-right tabular-nums">{range(m.range_low, m.range_high)}</td>
                          <td className="py-2 pr-3 text-right tabular-nums">{m.p_zero_pct != null ? `${m.p_zero_pct.toFixed(1)}%` : '–'}</td>
                          <td className="py-2 pr-3 tabular-nums">{m.settled_races > 0 ? actualText(m) : '–'}</td>
                          <td className="py-2" title={m.settled_races > 0 ? `Likely range for the settled races: ${range(m.settled_range_low, m.settled_range_high)}` : undefined}>
                            <StatusBadge status={m.status} />
                          </td>
                        </tr>
                        {isOpen && <RaceRows meeting={m} />}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
